import {
  CATEGORY,
  categoryDisplayNames,
  ENTERPRISE_PARTNER_ID,
  GROUP_STATUS,
  LEAD_TYPE,
} from '@brighte-enterprise/common/constants';
import { EnterpriseApplicationStatus } from '@prisma/client';

import { UserRole } from '@app-types/User';
import { EnterprisePartner } from '@typeDefs/groups';

// Application statuses that may still be auto-cancelled when their quote is
// superseded (ET-1878). packages/service cannot import packages/core, so this
// list is duplicated here — it MUST stay in sync with
// `applicationCancellableStatuses` in packages/core/src/domain/constants.ts.
export const applicationCancellableStatuses: EnterpriseApplicationStatus[] = [
  EnterpriseApplicationStatus.QUOTE,
  EnterpriseApplicationStatus.IN_PROGRESS,
];

export enum FINANCIAL_PRODUCT_SLUG {
  Electrify2515 = 'electrify-2515',
}

export const states: Record<string, string> = {
  ACT: 'Australian Capital Territory',
  NSW: 'New South Wales',
  NT: 'Northern Territory',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  VIC: 'Victoria',
  WA: 'Western Australia',
};

export const showContactRoles = [UserRole.AdminOperations, UserRole.EnterpriseConcierge, UserRole.EnterprisePartnerPII];

export enum ALERT_LEVEL {
  Warning = 'warning',
  Critical = 'critical',
}

export const pipelineAlertThresholds: Record<ALERT_LEVEL, number> = {
  [ALERT_LEVEL.Critical]: 15,
  [ALERT_LEVEL.Warning]: 10,
};

export const newStatusStaleDays = 10;

export const allowedRolesForEnterprisePartners = [
  UserRole.Team,
  UserRole.Vendor,
  UserRole.Manager,
  UserRole.AdminOperations,
  UserRole.Admin,
  UserRole.SuperAdmin,
  UserRole.EnterpriseConcierge,
  UserRole.Service,
];

// Backward compatibility alias
export const allowedRolesForEnterpriseGroups = allowedRolesForEnterprisePartners;

export const allowedRolesForDocuments = [UserRole.SuperAdmin, UserRole.Admin, UserRole.AdminOperations];

export const VALID_FILE_EXTENSIONS = {
  IMAGES: ['jpg', 'jpeg', 'png'],
  DOCUMENTS: ['pdf'],
  SPREADSHEETS: ['csv'],
  ALL: ['jpg', 'jpeg', 'png', 'pdf', 'csv'],
};

export const internalRoles = [UserRole.SuperAdmin, UserRole.Admin, UserRole.AdminOperations];

// ── Shared pre-marketplace config fragments ───────────────────────────────────
// Used for both channel-level fields and backward-compat partner-level fields.

const SOLAR_BUNDLE_SUBCATEGORIES = {
  'solar-battery-ev-charger': ['solar-system', 'battery-storage', 'ev-charger'],
};

const COMMBANK_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'household', required: true },
  { id: 'upgrade-profile', required: true },
  { id: 'financing-interest', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  // `required: true` means the step is shown; the step itself is skippable
  // (the customer can continue without uploading a bill, INT-1134).
  { id: 'bill-upload', required: true },
  { id: 'contact', required: true },
];

const NSW_HESL_STEP_FLOW = [
  { id: 'eligibility-check', required: true },
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'bundle-categories', required: false },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const STANDARD_BUNDLE_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'bundle-categories', required: false },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

const OVO_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'bundle-categories', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  // No `bill-upload` here: the INT-1134 bill-upload step is releasing for
  // CommBank only for now (product call, 2026-06-30); OVO will be added later.
  { id: 'contact', required: true },
];

const IAG_STEP_FLOW = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  // No `categories` step: IAG launches with the MVP bundle only (INT-1302),
  // so it goes straight to `bundle-categories` like OVO.
  { id: 'bundle-categories', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

export const enterpriseGroups: EnterprisePartner[] = [
  {
    id: ENTERPRISE_PARTNER_ID.evee,
    name: 'Evee',
    type: LEAD_TYPE.Concierge,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.tas,
    name: 'TAS Government',
    type: LEAD_TYPE.Marketplace,
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.oneMillionWomen,
    name: 'One Million Women',
    type: LEAD_TYPE.Concierge,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      premiumLeads: false,
      intake: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.commbank,
    name: 'CommBank',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'commbank',
          financialProductId: 'commbank-residential',
          title: 'Home Energy Upgrades',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: COMMBANK_STEP_FLOW,
            stepConfiguration: {
              categories: { multiSelection: true, options: [] },
              recommendedVendors: { selectVendors: false, pickVendorsInMarketplace: true },
            },
          },
          // Customer "what happens next" content for the CBA journey — rendered
          // on the success page, Tracker > Track your progress, AND the customer
          // request-received email, which all read these same progressSteps
          // (INT-1133, "Prep for your first call"; copy signed off by Risk).
          // The email (brighte-emails, develop: src/emails/Merged/Customer/
          // 1-request-received.html) pulls these steps from channel config, so it
          // stays in sync automatically; brighte-emails#82 extends it to render a
          // 6th step. NOTE: ProgressStep is {title, description} only, so the
          // "Prepare for your call" checklist is flattened into prose here; extend
          // ProgressStep with an optional items[] field if design wants real bullets.
          progressSteps: [
            {
              title: 'Prepare for your call with the vendors',
              description:
                'Your vendors may ask for a few things to help them prepare a more accurate quote: your most recent electricity bill (particularly your usage in kWh, your current tariff type, and your energy retailer); your property type (house, townhouse, apartment) and whether you own or rent; any shading on your roof from trees, neighbouring buildings, or other structures; whether you already have solar and are adding a battery or other items, or are starting fresh; and your switchboard type if you know it, or a photo of it — this helps vendors assess whether any upgrades may be needed.',
            },
            {
              title: 'Vendors will be in touch',
              description:
                "How vendors reach out can vary — some are comfortable quoting over the phone using satellite imagery and a copy of your energy bill, while others prefer to visit the property first so they can assess your roof, switchboard, and setup in person. If you share the home with a partner or co-owner, try to have them available for the call or site visit, as most vendors prefer both decision-makers to be present. That first discussion will usually cover your current energy bills and usage, the outcome you're looking for — whether that's reducing your electricity costs, adding battery backup, going off-peak, or a combination — and what system size and setup would best suit your home. If anything is unclear, ask — a good vendor will welcome the questions.",
            },
            {
              title: 'Review and select package online',
              description: 'Brighte will email you your quote(s) when they’re ready to view.',
            },
            {
              title: 'Choose your payment option online',
              description:
                "You'll have the option to apply for one of CommBank's financing solutions or you can pay with a debit or credit card.",
            },
            {
              title: 'Installation',
              description:
                'Once your application for finance has been approved, or you have arranged payment with your chosen vendor, they will schedule the installation with you.',
            },
            {
              title: 'Payment',
              description:
                'If you have taken out finance, repayments to CommBank will start once installation is completed.',
            },
          ],
          heroImage: 'hero-cba-request-b',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      hasCoBrandedLogo: true,
      sparkThemeName: 'pantheon',
      backgroundColour: '#F4F4F4',
      cardBorderRadius: 'none',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: true,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: false,
      packageEnabled: true,
      pendingVendorFollowupEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      partnerWebsiteUrl: 'https://www.commbank.com.au',
      brighteSiteSlug: 'commbank',
      contactNumber: '1300 500 107',
      marketplaceFlag: 'commbank_residential',
      leadPricing: 35,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.racq,
    name: 'RACQ',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'racq',
          financialProductId: 'brighte-pay',
          title: 'Home Upgrade Program',
          cmsContentSlug: 'racq',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: STANDARD_BUNDLE_STEP_FLOW,
            stepConfiguration: {
              categories: {
                multiSelection: false,
                options: ['solar-battery-ev-charger', 'hot-water-heat-pumps-hwhp', 'water-filtration'],
              },
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: false,
              },
            },
          },
          // Copy locked to the consolidated lead-received email (INT-1147), template
          // brighte-emails src/emails/Customer/PackageEnabled/request-received.html —
          // keep in sync with any change there.
          progressSteps: [
            {
              title: 'Vendors will be in touch',
              description:
                'Once vendors receive your request, they will contact you in the next couple of days to discuss your request and book a site visit.',
            },
            {
              title: 'Review and accept quotes online',
              description:
                'Brighte will email you when your quotes are ready to review. You can select the quote package that best fits your needs and budget.',
            },
            {
              title: 'Choose your payment option online',
              description:
                "You'll have the option to apply for one of Brighte's financing solutions or you can pay with a debit or credit card.",
            },
            {
              title: 'Installation',
              description:
                'Once your application for finance has been approved, or you have arranged payment with your chosen vendor, they will schedule the installation with you.',
            },
          ],
          heroImage: 'racq-hero',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      logoWidth: 90,
      sparkThemeName: 'bravo',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      customerIdRequirement: 'optional',
      hasMarketingOptIn: true,
      hasCustomAssetPath: true,
      vendorBrowsingEnabled: false,
      filterVendorByPartner: true,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      brighteSiteSlug: 'racq',
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_racq',
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.ovoEnergy,
    name: 'OVO Energy',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'ovo-energy',
          financialProductId: 'brighte-pay',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: OVO_STEP_FLOW,
            stepConfiguration: {
              // No `categories` block — OVO_STEP_FLOW has no `categories` step (goes straight to `bundle-categories`).
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: false,
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: true,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_ovo',
      leadPricing: 45,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.iag,
    name: 'IAG',
    type: LEAD_TYPE.Marketplace,
    // prod starts inactive: not yet launched (target go-live 2026-08-03 per
    // the IAG Marketplace Approach for Launch doc). staging/uat are active
    // so QA can exercise both channels ahead of go-live.
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.inactive },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          // IAG Staff Offer: internal link to a dedicated Brighte Landing Page.
          slug: 'iag',
          // No financialProductId: IAG wants vendors to offer whichever
          // finance product they're accredited for, and to skip the package
          // screen experience entirely.
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: IAG_STEP_FLOW,
            stepConfiguration: {
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: false,
              },
            },
          },
        },
        {
          // NRMA Rewards: tile embedded in the NRMA Insurance App.
          slug: 'nrma-rewards',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
          preMarketplace: {
            stepFlow: IAG_STEP_FLOW,
            stepConfiguration: {
              bundleCategories: { enabled: true, subcategories: SOLAR_BUNDLE_SUBCATEGORIES },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: false,
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: true,
      hasMarketingOptIn: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: true,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
      marketplaceFlag: 'marketplace_iag',
      leadPricing: 35,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.rewiringAustralia,
    name: 'Rewiring Australia',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: true, packageSupportsMultipleVendors: true },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'electrify-2515',
          financialProductId: 'electrify-2515',
          fixedPostcode: '2515',
          title: 'Electrify 2515 Community Pilot',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
          leadLabel: 'Electrify 2515 Community Pilot',
          vendorGuidelineUrl: 'https://brighte.com.au/electrify-2515-vendor-guide',
          requiredCategories: [
            { slug: CATEGORY.SmartEnergyDevice, name: categoryDisplayNames[CATEGORY.SmartEnergyDevice] },
          ],
          // Copy locked to the consolidated lead-received email (INT-1147), template
          // brighte-emails src/emails/Customer/PackageEnabled/request-received.html —
          // keep in sync with any change there.
          progressSteps: [
            {
              title: 'Vendors will contact you',
              description: 'We have sent your request to the selected vendors.',
            },
            {
              title: 'Site visit and quoting',
              description: 'We recommend that vendors visit your home to provide you with the best solutions.',
            },
            {
              title: 'Application',
              description:
                'You will be able to choose your preferred vendors and equipment, and apply for subsidies and finance if you need it.',
            },
            {
              title: 'Installation',
              description: 'Your selected vendors will install your new equipment.',
            },
            {
              title: 'Payment',
              description:
                'After installation, your repayments will begin to Brighte or full payment is made to your selected vendor. Any subsidies will be deducted before payment.',
            },
          ],
          heroImage: 'electrify-2515-hero',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: true,
      allowLeadCreation: false,
      multiVendor: true,
      intake: true,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: false,
      packageEnabled: true,

      // ── Links & References ────────────────────────────────────────────────
      partnerWebsiteUrl: 'https://www.electrify2515.org/pilot',
      brighteSiteSlug: 'electrify-2515',
      contactNumber: '1300 135 100',
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.act,
    name: 'ACT Government',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'act',
          financialProductId: 'act-shs-loan',
          title: 'ACT Sustainable Household Scheme',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
        {
          slug: 'act-community',
          financialProductId: 'act-community-loan',
          title: 'ACT Community Household Scheme',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
        {
          slug: 'act-sfa',
          financialProductId: 'act-solar-for-apartments-loan',
          title: 'ACT Solar for Apartments',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
        // Product ID uses the original internal name (SLIP = Solar for Low Income Programme); renamed to HESP before launch but ID was not changed.
        {
          slug: 'act-hesp',
          financialProductId: 'act-slip-loan',
          title: 'ACT Home Energy Support Program',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: true,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.brighte,
    name: 'Brighte',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'brighte',
          showTitleInMarketplace: false,
          vendorSortingPreference: 'RANKING',
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      hasPromotionContent: true,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
    },
  },
  {
    id: ENTERPRISE_PARTNER_ID.nswGov,
    name: 'NSW Government',
    type: LEAD_TYPE.Marketplace,
    status: { staging: GROUP_STATUS.active, uat: GROUP_STATUS.active, prod: GROUP_STATUS.active },
    referralConfiguration: { referralSupportsMultipleCategories: false, packageSupportsMultipleVendors: false },
    configuration: {
      // ── Channels ──────────────────────────────────────────────────────────
      channels: [
        {
          slug: 'nsw-home-energy-saver',
          financialProductId: 'brighte-home-energy-saver-loan',
          title: 'NSW Home Energy Saver Loan',
          showTitleInMarketplace: true,
          vendorSortingPreference: 'RANKING',
          leadLabel: 'Brighte Home Energy Saver Loan',
          vendorGuidelineUrl: 'https://brighte.com.au/nsw-vendors',
          vendorEmail: {
            leadReceivedPanel: {
              heading: 'About the loan',
              body: 'NSW Government’s Home Energy Saver Program is here — offering eligible households up to $15,000 at 0% interest for solar, batteries, heating and cooling, and more.',
              ctaLabel: 'Learn more',
            },
          },
          preMarketplace: {
            stepFlow: NSW_HESL_STEP_FLOW,
            stepConfiguration: {
              categories: {
                multiSelection: false,
                options: [
                  'solar-battery-system',
                  'hot-water-heat-pumps-hwhp',
                  'electric-heating-cooling-systems-reverse-cycle',
                  'ev-charging-infrastructure',
                ],
              },
              bundleCategories: {
                enabled: true,
                subcategories: { 'solar-battery-system': ['solar-system', 'battery-storage'] },
              },
              recommendedVendors: {
                selectVendors: true,
                pickVendorsInMarketplace: true,
              },
              introduction: {
                introductionType: 'BRIGHTE_INTRODUCTION',
                stepGuideLink: 'https://brighte.com.au/homeowners/nsw-home-energy-saver-loan/next-steps',
              },
              eligibilityCheck: {
                items: [
                  'Your installation address must be in NSW',
                  "Your household's total taxable income must be under $210,000",
                ],
              },
            },
          },
        },
      ],

      // ── Branding ──────────────────────────────────────────────────────────
      sparkThemeName: 'brighte',
      backgroundColour: '#F1F4FF',
      cardBorderRadius: 'large',

      // ── Behaviour ─────────────────────────────────────────────────────────
      requireLead: false,
      allowLeadCreation: true,
      multiVendor: false,
      intake: false,
      premiumLeads: false,
      hasMarketingOptIn: false,
      // No promotion content for nsw-gov: the Hygraph PromotionPartner enum has no
      // `nswgov` value (only act/brighte/commbank/rewiringau), so the marketplace
      // promotions query would 400. Keep this false to skip the promotions fetch
      // entirely (INT-1178).
      hasPromotionContent: false,
      vendorBrowsingEnabled: true,
      filterVendorByPartner: false,
      redirectToSuccessPage: true,
      packageEnabled: false,

      // ── Links & References ────────────────────────────────────────────────
      contactNumber: '1300 274 448',
    },
  },
];
